<script setup lang="ts">
import {ref} from 'vue';
import {VidalInteraction} from '@/drug-interaction/interfaces';
import {InteractionGroup, interactionKey} from '@/drug-interaction/composables/useInteractionGroups';
import InteractionRow from '@/drug-interaction/components/alerts/InteractionRow.vue';
import {Checkbox} from '@/core/components/ui/checkbox';
import {Textarea} from '@/core/components/ui/textarea';
import {ALERT_TYPE} from '@/drug-interaction/interfaces';
import InteractionTagsList from "@/drug-interaction/components/alerts/InteractionTagsList.vue";

const props = defineProps<{
    groups: InteractionGroup[];
    checkedKeys: Set<string>;
    comments: Record<number, string>;
    /**
     * Alert types active by default (pre-selected filters).
     * If omitted, all types are shown.
     */
    primaryTypes?: ALERT_TYPE[];
}>();

const emits = defineEmits<{
    (e: 'toggle', interaction: VidalInteraction): void;
    (e: 'update:comments', id: number, value: string): void;
}>();

const activeTypesByLine = ref<Record<number, Set<ALERT_TYPE>>>({});

/**
 * Get active alert types for a given line.
 * Initializes with defaults if not already set.
 */
const getActiveTypes = (group: InteractionGroup): Set<ALERT_TYPE> => {
    if (!activeTypesByLine.value[group.item.id]) {
        const typesInGroup = [...new Set(group.interactions.map(i => i.type))];
        const defaults = props.primaryTypes
            ? typesInGroup.filter(t => props.primaryTypes!.includes(t))
            : typesInGroup;
        activeTypesByLine.value[group.item.id] = new Set(defaults);
    }
    return activeTypesByLine.value[group.item.id];
};


/**
 * Filter interactions for a given group based on active alert types.
 */
const filteredInteractions = (group: InteractionGroup): VidalInteraction[] => {
    const active = getActiveTypes(group);
    return group.interactions.filter(i => active.has(i.type));
};
</script>

<template>
  <div class="overflow-y-auto min-h-0 flex-1 flex flex-col gap-4">
    <p v-if="groups.length === 0" class="text-sm text-gray-500">
      Aucune interaction détectée.
    </p>

    <template v-else>
      <div v-for="group in groups" :key="group.item.id" class="flex flex-col gap-2">
        <h3 class="text-sm font-semibold text-gray-800">
          {{ group.item.name }}
        </h3>

        <InteractionTagsList
          :group="group"
          :checked-keys="checkedKeys"
          :active-types="getActiveTypes(group)"
          @update-active-types-by-line="(id, active) => activeTypesByLine[id] = active"
        />

        <div class="border rounded-lg bg-gray-50 flex flex-col divide-y">
          <p v-if="filteredInteractions(group).length === 0" class="px-3 py-2 text-xs text-gray-400 italic">
            Aucune alerte pour les filtres sélectionnés.
          </p>

          <div
            v-for="interaction in filteredInteractions(group)"
            :key="interactionKey(interaction)"
            class="flex items-start gap-3 px-3 py-2"
          >
            <Checkbox
              :model-value="checkedKeys.has(interactionKey(interaction))"
              class="mt-1 shrink-0"
              @update:model-value="emits('toggle', interaction)"
            />
            <InteractionRow :interaction="interaction" />
          </div>

          <div class="px-3 py-2">
            <Textarea
              :model-value="comments[group.item.id]"
              class="w-full bg-white text-xs resize-none"
              placeholder="Commentaire libre..."
              rows="2"
              @update:model-value="emits('update:comments', group.item.id, $event as string)"
            />
          </div>
        </div>
      </div>
    </template>
  </div>
</template>
